import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loader2, Sparkles, X } from 'lucide-react';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { roleRank, TEMPLATE_ACTIONS_MIN_RANK } from '@constants/roleHierarchy';
import { useAiPlanningAvailable, useDiscardProjectPlanDraft, useMyProjectPlanDrafts } from '@hooks/api/projectPlanDrafts';
import { ProjectPlanDraft } from '@models/projectPlanDraft.model';
import { draftResumePath, planProgressLabel } from '@utils/planProgress';
import { useTranslation } from '@hooks/useTranslation';

interface InProgressAiPlansProps {
  /** Show the link with an empty-state popover even when there are no drafts (wizard plan step). */
  showWhenEmpty?: boolean;
  /**
   * `toggle` (default): the collapsed "Resume an AI plan (n)" link. `expanded`: a card headed
   * "You have N AI plans in progress" with the list always open and a Resume action per draft —
   * the discoverable entry point (Projects sidebar, wizard step 1). Renders nothing when there
   * are no drafts, while loading, or on a load error.
   */
  variant?: 'toggle' | 'expanded';
  /** Leave this draft out of the list (the one already open on the page). */
  excludeDraftId?: string;
}

/**
 * Sprint 36, story-189. "Resume an AI plan (n)" entry point listing the caller's GENERATING and
 * READY drafts and reopening each on the route that matches its target. Hidden when AI planning
 * is off or the role is below SUPERINTENDENT. Draft ids live in the URL, one per draft, so
 * several can run at once without cross-wiring.
 */
export const InProgressAiPlans: React.FC<InProgressAiPlansProps> = ({ showWhenEmpty = false, variant = 'toggle', excludeDraftId }) => {
  const t = useTranslation('aiPlan');
  const history = useHistory();
  const { role } = useAppSelector((state: RootState) => state.user);
  const { data: available } = useAiPlanningAvailable();
  const allowed = !!available && roleRank(role) >= TEMPLATE_ACTIONS_MIN_RANK;
  const { data: allDrafts = [], isLoading, isError, refetch } = useMyProjectPlanDrafts(allowed);
  const drafts = excludeDraftId ? allDrafts.filter((d) => d.draftId !== excludeDraftId) : allDrafts;
  const [open, setOpen] = useState(false);
  // Draft awaiting a "Yes, discard" / "Keep" answer; one at a time.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const discardMutation = useDiscardProjectPlanDraft();

  if (!allowed) return null;

  const discardDraft = async (draft: ProjectPlanDraft) => {
    try {
      await discardMutation.mutateAsync(draft.draftId);
      toast.success(t.draftDiscarded);
    } catch {
      toast.error(t.draftDiscardFailed);
    } finally {
      setConfirmingId(null);
    }
  };

  /**
   * Per-draft dismiss control. A GENERATING draft reads "Cancel" (the server aborts the model
   * call), a READY one "Discard"; either asks once, inline, before the mutation fires.
   */
  const discardControl = (draft: ProjectPlanDraft, compact: boolean) => {
    const isGenerating = draft.status === 'GENERATING';
    if (confirmingId === draft.draftId) {
      return (
        <div className="flex items-center gap-2 text-[11px]" data-testid={`in-progress-ai-plan-confirm-${draft.draftId}`} role="group">
          <span className="text-gray-700 dark:text-gray-300">{isGenerating ? t.discardConfirmGenerating : t.discardConfirmReady}</span>
          <button
            type="button"
            onClick={() => discardDraft(draft)}
            disabled={discardMutation.isPending}
            data-testid={`in-progress-ai-plan-discard-confirm-${draft.draftId}`}
            className="px-2 py-1 font-medium rounded-md bg-red-600 text-white disabled:opacity-60">
            {t.discardConfirmYes}
          </button>
          <button
            type="button"
            onClick={() => setConfirmingId(null)}
            disabled={discardMutation.isPending}
            data-testid={`in-progress-ai-plan-discard-keep-${draft.draftId}`}
            className="px-2 py-1 font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
            {t.discardConfirmNo}
          </button>
        </div>
      );
    }
    const label = isGenerating ? t.cancelDraft : t.discardDraft;
    return (
      <button
        type="button"
        onClick={() => setConfirmingId(draft.draftId)}
        aria-label={`${label}: ${draft.title || t.untitledDraft}`}
        title={label}
        data-testid={`in-progress-ai-plan-discard-${draft.draftId}`}
        className={
          compact
            ? 'flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30'
            : 'flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-red-400 hover:text-red-600'
        }>
        {compact ? <X className="w-3.5 h-3.5" aria-hidden /> : label}
      </button>
    );
  };

  const contextLabel = (draft: ProjectPlanDraft) =>
    draft.projectId ? t.draftExistingProject : draft.purpose === 'TEMPLATE' ? t.draftTemplate : t.draftNewProject;

  const statusLine = (draft: ProjectPlanDraft) =>
    draft.status === 'GENERATING' ? (
      <>
        <Loader2 className="w-3 h-3 animate-spin motion-reduce:animate-none" aria-hidden />
        {planProgressLabel(t, draft.progress)}
      </>
    ) : (
      <>
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" aria-hidden />
        {t.statusReady}
      </>
    );

  if (variant === 'expanded') {
    // Walkthrough fix (sprint 38): drafts were only reachable behind a collapsed toggle; list them openly.
    if (isLoading || isError || drafts.length === 0) return null;
    return (
      <section
        className="w-full rounded-lg border border-primary/30 bg-indigo-50 dark:bg-gray-800 p-3 text-left"
        aria-labelledby="in-progress-ai-plans-title"
        data-testid="in-progress-ai-plans-expanded">
        <h2 id="in-progress-ai-plans-title" className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0" aria-hidden />
          {drafts.length === 1 ? t.inProgressTitleOne : t.inProgressTitle.replace('{n}', String(drafts.length))}
        </h2>
        <ul className="mt-2 flex flex-col gap-2 max-h-64 overflow-y-auto">
          {drafts.map((draft) => (
            <li
              key={draft.draftId}
              data-testid={`in-progress-ai-plan-${draft.draftId}`}
              className="flex flex-wrap items-center gap-2 rounded-md bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 px-3 py-2">
              {/* Wraps the Resume button under the text in narrow slots (Projects sidebar). */}
              <div className="min-w-[9rem] flex-1">
                <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{draft.title || t.untitledDraft}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {contextLabel(draft)} · {new Date(draft.createdAt).toLocaleString()}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300">{statusLine(draft)}</p>
              </div>
              {confirmingId !== draft.draftId && (
                <button
                  type="button"
                  onClick={() => history.push(draftResumePath(draft))}
                  data-testid={`in-progress-ai-plan-resume-${draft.draftId}`}
                  className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-white">
                  {t.resumeDraft}
                </button>
              )}
              {discardControl(draft, false)}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (!showWhenEmpty && !isLoading && !isError && drafts.length === 0) return null;

  return (
    <div className="relative" data-testid="in-progress-ai-plans">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="in-progress-ai-plans-toggle"
        className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
        <Sparkles className="w-3.5 h-3.5" aria-hidden />
        {t.resumeLink.replace('{n}', String(drafts.length))}
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm" data-testid="in-progress-ai-plans-list">
          <p className="px-3 pt-2 text-xs font-semibold text-gray-700 dark:text-gray-300">{t.panelTitle}</p>
          {isLoading && <p className="px-3 py-2 text-xs text-gray-400">…</p>}
          {isError && (
            <p className="px-3 py-2 text-xs text-red-600">
              {t.draftsLoadError}{' '}
              <button type="button" onClick={() => refetch()} className="underline">
                {t.retry}
              </button>
            </p>
          )}
          {!isLoading && !isError && drafts.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">{t.resumeEmpty}</p>}
          <ul>
            {drafts.map((draft) => (
              <li key={draft.draftId} className="border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-start gap-1 pr-2">
                  <button
                    type="button"
                    onClick={() => history.push(draftResumePath(draft))}
                    data-testid={`in-progress-ai-plan-${draft.draftId}`}
                    className="min-w-0 flex-1 text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{draft.title || t.untitledDraft}</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {contextLabel(draft)} · {new Date(draft.createdAt).toLocaleString()}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300">
                      {statusLine(draft)}
                      <span className="ml-auto underline">{t.openDraft}</span>
                    </p>
                  </button>
                  {confirmingId !== draft.draftId && <div className="pt-2">{discardControl(draft, true)}</div>}
                </div>
                {confirmingId === draft.draftId && <div className="px-3 pb-2">{discardControl(draft, true)}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InProgressAiPlans;
